import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: 'fa-house' },
    { name: 'Ürün Yönetimi', path: '/admin/products', icon: 'fa-box' },
    { name: 'Siparişler', path: '/admin/orders', icon: 'fa-cart-shopping' },
    { name: 'İade Talepleri', path: '/admin/returns', icon: 'fa-rotate-left' },
    { name: 'Kategoriler', path: '/admin/categories', icon: 'fa-folder' },
    // { name: 'Site Metinleri', path: '/admin/content', icon: 'fa-pen-to-square' },
    { name: 'Sayfa İçerikleri', path: '/admin/page-content', icon: 'fa-newspaper' },
    { name: 'Müşteriler', path: '/admin/customers', icon: 'fa-users' },
    { name: 'Kuponlar', path: '/admin/coupons', icon: 'fa-ticket' },
    { name: 'Bannerlar', path: '/admin/banners', icon: 'fa-bullhorn' },
    { name: '3D Lezzet Kataloğu', path: '/admin/flipbook', icon: 'fa-book-open' },
    { name: 'Paket Kampanyalar', path: '/admin/deal-campaigns', icon: 'fa-gift' },
    { name: 'Koleksiyonlar', path: '/admin/collections', icon: 'fa-layer-group' },
    { name: 'Yorumlar', path: '/admin/reviews', icon: 'fa-star' },
    { name: 'Raporlar', path: '/admin/reports', icon: 'fa-chart-column' },
    { name: 'Kargo Ayarları', path: '/admin/cargo-settings', icon: 'fa-truck' },
    { name: 'Teslimat Programları', path: '/admin/delivery-schedules', icon: 'fa-calendar-days' },
    { name: 'Ödeme Ayarları', path: '/admin/payment-settings', icon: 'fa-credit-card' },
    { name: 'İşlem Kayıtları', path: '/admin/audit-logs', icon: 'fa-clipboard-list' },
    { name: 'E-posta Şablonları', path: '/admin/email-templates', icon: 'fa-envelope-open-text' },
    // Yönetici atama yalnızca süper yöneticide; erişim kontrolü backend'de zorunlu tutulur
    ...(user?.adminRole === 'SUPER_ADMIN'
      ? [{ name: 'Yöneticiler', path: '/admin/admins', icon: 'fa-user-shield' }]
      : []),
    { name: 'Ayarlar', path: '/admin/settings', icon: 'fa-gear' },
  ];

  const pageName = menuItems.find((item) => item.path === location.pathname)?.name || 'Dashboard';

  const showLabels = mobileOpen || sidebarOpen;

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden font-admin text-gray-800">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/40 z-50 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-[60] w-64 bg-white border-r border-gray-200 transition-all duration-200 ease-in-out flex flex-col transform ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:z-50 ${sidebarOpen ? 'lg:w-64' : 'lg:w-[72px]'}`}
      >
        <div className="h-16 flex items-center justify-between border-b border-gray-200 px-4">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <span className="shrink-0 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">Aİ</span>
            {showLabels && <span className="text-gray-900 font-semibold text-[15px] whitespace-nowrap">Admin Panel</span>}
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100"
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 custom-scrollbar">
          <div className="space-y-0.5">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  title={!showLabels ? item.name : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <i className={`fas ${item.icon} w-4 text-center ${isActive ? 'text-blue-600' : 'text-gray-400'}`}></i>
                  {showLabels && <span className="truncate">{item.name}</span>}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-3 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
          >
            <i className="fas fa-power-off w-4 text-center"></i>
            {showLabels && <span>Güvenli Çıkış</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 relative z-40">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-bars text-sm"></i>
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex w-9 h-9 rounded-lg border border-gray-200 items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <i className={`fas ${sidebarOpen ? 'fa-bars-staggered' : 'fa-bars'} text-sm`}></i>
            </button>
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-gray-400">Yönetim</span>
              <i className="fas fa-chevron-right text-[10px] text-gray-300"></i>
              <span className="text-gray-900 font-medium">{pageName}</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              <i className="fas fa-arrow-up-right-from-square text-xs"></i>
              <span className="hidden sm:inline">Siteye Git</span>
            </button>

            <button className="relative w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
              <i className="far fa-bell text-base"></i>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-600 rounded-full border-2 border-white"></span>
            </button>

            <div className="h-8 w-px bg-gray-200"></div>

            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-400 leading-tight">
                  {user?.adminRole === 'SUPER_ADMIN' ? 'Süper Yönetici' : 'Yönetici'}
                </p>
              </div>
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center border border-gray-200 overflow-hidden">
                {user?.profileImage ? (
                  <img src={user.profileImage} alt="User" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-blue-600 font-semibold text-sm">{user?.firstName?.[0]}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
